import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { LeadEnrichment } from "@/components/leads/LeadEnrichment";
import { ChannelPreferences } from "@/components/distribution/ChannelPreferences";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Settings, UserPlus, Trash, Archive, Edit, CheckSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { LeadTableHeader } from "@/components/leads/LeadTableHeader";
import { LeadTablePagination } from "@/components/leads/LeadTablePagination";
import { LeadFilters } from "@/components/leads/LeadFilters";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import type { NewsletterLead } from "./types";
import { LeadEditForm } from "./LeadEditForm";

interface LeadTableProps {
  leads: NewsletterLead[];
  isLoading: boolean;
}

const ITEMS_PER_PAGE = 25;

export const LeadTable = ({ leads: initialLeads, isLoading }: LeadTableProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const { toast } = useToast();

  const { data, isLoading: isLeadsLoading, refetch } = useQuery({
    queryKey: ['newsletter-leads', currentPage, searchQuery, selectedCategory],
    queryFn: async () => {
      let query = supabase
        .from('newsletter_leads')
        .select('*', { count: 'exact' });

      if (searchQuery) {
        query = query.or(`email.ilike.%${searchQuery}%,full_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%,company.ilike.%${searchQuery}%`);
      }

      if (selectedCategory !== 'all') {
        query = query.eq('category', selectedCategory);
      }

      const start = (currentPage - 1) * ITEMS_PER_PAGE;
      query = query
        .range(start, start + ITEMS_PER_PAGE - 1)
        .order('created_at', { ascending: false });

      const { data: leads, error, count } = await query;

      if (error) {
        console.error('Error fetching leads:', error);
        return { leads: [], totalCount: 0 };
      }

      return { 
        leads: leads as NewsletterLead[], 
        totalCount: count || 0 
      };
    },
  });

  const totalPages = Math.ceil((data?.totalCount || 0) / ITEMS_PER_PAGE);

  const toggleLeadSelection = (leadId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const toggleAllLeads = () => {
    if (data?.leads) {
      if (selectedLeads.length === data.leads.length) {
        setSelectedLeads([]);
      } else {
        setSelectedLeads(data.leads.map(lead => lead.id));
      }
    }
  };

  const handleBulkDelete = async () => {
    try {
      const { error } = await supabase
        .from('newsletter_leads')
        .delete()
        .in('id', selectedLeads);

      if (error) throw error;

      toast({
        title: "Success",
        description: `${selectedLeads.length} leads deleted successfully`,
      });
      
      setSelectedLeads([]);
      refetch();
    } catch (error) {
      console.error('Error deleting leads:', error);
      toast({
        title: "Error",
        description: "Failed to delete leads",
        variant: "destructive",
      });
    }
  };

  const handleBulkArchive = async () => {
    try {
      const { error } = await supabase
        .from('newsletter_leads')
        .update({ subscription_status: 'archived' })
        .in('id', selectedLeads);

      if (error) throw error;

      toast({
        title: "Success",
        description: `${selectedLeads.length} leads archived successfully`,
      });
      
      setSelectedLeads([]);
      refetch();
    } catch (error) {
      console.error('Error archiving leads:', error);
      toast({
        title: "Error",
        description: "Failed to archive leads",
        variant: "destructive",
      });
    }
  };

  const handleSingleDelete = async (leadId: string) => {
    try {
      const { error } = await supabase
        .from('newsletter_leads')
        .delete()
        .eq('id', leadId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Lead deleted successfully",
      });
      
      refetch();
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast({
        title: "Error",
        description: "Failed to delete lead",
        variant: "destructive",
      });
    }
  };

  if (isLoading || isLeadsLoading) {
    return <div>Loading leads...</div>;
  }

  return (
    <div className="space-y-4 w-full max-w-[95vw] mx-auto">
      <LeadFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
      />

      {selectedLeads.length > 0 && (
        <div className="flex gap-2 mb-4">
          <Button
            variant="destructive"
            size="sm"
            onClick={handleBulkDelete}
            className="flex items-center gap-2"
          >
            <Trash className="h-4 w-4" />
            Delete Selected ({selectedLeads.length})
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleBulkArchive}
            className="flex items-center gap-2"
          >
            <Archive className="h-4 w-4" />
            Archive Selected ({selectedLeads.length})
          </Button>
        </div>
      )}

      <div className="rounded-md border overflow-hidden">
        <Table>
          <LeadTableHeader onSelectAll={toggleAllLeads} allSelected={data?.leads && selectedLeads.length === data.leads.length} />
          <TableBody>
            {data?.leads?.map((lead) => (
              <TableRow 
                key={lead.id}
                className={cn(
                  "cursor-pointer hover:bg-muted/50",
                  selectedLeads.includes(lead.id) && "bg-muted"
                )}
              >
                <TableCell className="w-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      "p-0 h-auto",
                      selectedLeads.includes(lead.id) && "text-primary"
                    )}
                    onClick={(e) => toggleLeadSelection(lead.id, e)}
                  >
                    <CheckSquare className="h-4 w-4" />
                  </Button>
                </TableCell>
                <TableCell>{lead.email}</TableCell>
                <TableCell>{lead.full_name}</TableCell>
                <TableCell>{lead.last_name}</TableCell>
                <TableCell>{lead.company}</TableCell>
                <TableCell>{lead.role_title}</TableCell>
                <TableCell>{lead.category}</TableCell>
                <TableCell>{lead.subscription_status}</TableCell>
                <TableCell>
                  <div className="flex space-x-2" onClick={(e) => e.stopPropagation()}>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <UserPlus className="h-4 w-4 mr-2" />
                          Enrich
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <LeadEnrichment 
                          lead={lead}
                          onEnrichmentComplete={() => {
                            refetch();
                          }}
                        />
                      </DialogContent>
                    </Dialog>
                    
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Settings className="h-4 w-4 mr-2" />
                          Channels
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <ChannelPreferences leadId={lead.id} />
                      </DialogContent>
                    </Dialog>

                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleSingleDelete(lead.id)}
                    >
                      <Trash className="h-4 w-4 mr-2" />
                      Delete
                    </Button>

                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <LeadEditForm 
                          lead={lead}
                          onSuccess={() => {
                            refetch();
                            const closeButton = document.querySelector('[data-dialog-close]');
                            if (closeButton instanceof HTMLElement) {
                              closeButton.click();
                            }
                          }}
                          onCancel={() => {
                            const closeButton = document.querySelector('[data-dialog-close]');
                            if (closeButton instanceof HTMLElement) {
                              closeButton.click();
                            }
                          }}
                        />
                      </DialogContent>
                    </Dialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <LeadTablePagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};
